/**
 * Indonesian translations — the source of truth for the
 * translation tree. Every other locale file must mirror this
 * structure exactly; the bilingual-parity test enforces it.
 *
 * Tone: enterprise-formal. Use "Anda" (never "kamu"), spell out
 * abbreviations, and keep wording abstraction-safe per the
 * terminology standard in apps/web/I18N_GLOSSARY.md.
 */
export const id = {
  common: {
    save: "Simpan",
    cancel: "Batal",
    delete: "Hapus",
    edit: "Edit",
    add: "Tambah",
    remove: "Hapus",
    loading: "Memuat...",
    saving: "Menyimpan...",
    processing: "Memproses...",
    error: "Terjadi kesalahan",
    errorGeneric:
      "Terjadi kesalahan. Silakan coba lagi, atau hubungi support jika masalah berlanjut.",
    success: "Berhasil",
    confirm: "Konfirmasi",
    close: "Tutup",
    back: "Kembali",
    next: "Lanjut",
    retry: "Coba lagi",
    search: "Cari",
    filter: "Filter",
    actions: "Aksi",
    status: "Status",
    created: "Dibuat",
    updated: "Diperbarui",
    name: "Nama",
    description: "Deskripsi",
    optional: "Opsional",
    required: "Wajib diisi",
    all: "Semua",
    none: "Tidak ada",
    yes: "Ya",
    no: "Tidak",
  },

  auth: {
    brand: {
      eyebrow: "Monago Atrium",
      headline: "Satu lapisan kontrol untuk semua AI di bisnis Anda.",
      subhead:
        "Redaksi PII, enforcement policy, dan audit trail — di setiap panggilan AI, secara default.",
      panelAriaLabel: "Sorotan Monago Atrium",
      footerCopyright: "© Monago. Bagian dari platform Monago.",
      visibility: {
        title: "Visibilitas penuh",
        body:
          "Setiap permintaan AI tercatat — siapa, kapan, untuk apa, berapa biayanya, model mana. Dashboard per tim, aplikasi, dan proyek.",
      },
      policy: {
        title: "Policy & access control",
        body:
          "Tentukan tim mana pakai model mana. Atur kuota. Filter data sensitif sebelum sampai ke AI publik.",
      },
      multiProvider: {
        title: "Multi-provider, tanpa lock-in",
        body:
          "OpenAI, Claude, Gemini, open-source, model internal — satu integrasi. Ganti provider tanpa ubah kode.",
      },
    },
    login: {
      title: "Masuk ke Atrium",
      subtitle: "Tata kelola dan observability untuk infrastruktur AI",
      emailLabel: "Email",
      emailPlaceholder: "nama@perusahaan.id",
      passwordLabel: "Password",
      submitButton: "Masuk",
      submitLoading: "Memproses...",
      noAccount: "Belum punya akun?",
      registerLink: "Daftar dengan invitation code",
      errors: {
        invalidCredentials: "Email atau password salah.",
        rateLimited:
          "Terlalu banyak percobaan masuk. Tunggu beberapa menit, lalu coba lagi.",
        unexpected:
          "Terjadi kesalahan yang tidak terduga. Silakan coba lagi.",
      },
    },
    register: {
      haveAccount: "Sudah punya akun?",
      loginLink: "Masuk di sini",
      step1: {
        title: "Daftar akun baru",
        subtitle: "Langkah 1 dari 3 — Informasi dasar",
        fullNameLabel: "Nama lengkap",
        fullNamePlaceholder: "Nama lengkap Anda",
        emailLabel: "Email",
        emailPlaceholder: "nama@perusahaan.id",
        nextButton: "Lanjut",
      },
      step2: {
        title: "Invitation code",
        subtitle: "Langkah 2 dari 3 — Masukkan signup key dari admin",
        keyLabel: "Signup key",
        keyPlaceholder: "msk_xxxxxxxxxxxxxxxxxx",
        keyHint:
          "Format msk_ diikuti 28 karakter. Belum memilikinya? Hubungi admin organisasi Anda.",
        backButton: "Kembali",
        validateButton: "Validasi",
        validateLoading: "Memvalidasi...",
        errors: {
          invalidKey: "Signup key tidak valid atau sudah digunakan.",
        },
      },
      step3: {
        title: "Selesaikan pendaftaran",
        subtitle: "Langkah 3 dari 3 — Buat password",
        tenantLabel: "Organisasi",
        passwordLabel: "Password",
        passwordPlaceholder: "Minimal {min} karakter",
        passwordHintDefault:
          "Minimal {min} karakter — kombinasi huruf besar, huruf kecil, angka, dan simbol.",
        passwordHintShort: "Kurang {remaining} karakter lagi.",
        confirmLabel: "Konfirmasi password",
        backButton: "Kembali",
        submitButton: "Selesai",
        submitLoading: "Membuat akun...",
        errors: {
          tooShort: "Password harus minimal {min} karakter.",
          mismatch: "Konfirmasi password tidak cocok.",
          registerFailed:
            "Signup key sudah digunakan, atau password belum memenuhi ketentuan keamanan.",
        },
      },
    },
  },

  navigation: {
    groups: {
      main: "Utama",
      aiOperations: "Operasi AI",
      governance: "Governance",
      preferences: "Pengaturan",
      platform: "Platform",
    },
    items: {
      dashboard: "Dashboard",
      apiKeys: "Kunci API",
      aiProviders: "Penyedia AI",
      observability: "Observability",
      playground: "Playground",
      policies: "Kebijakan",
      assets: "Aset AI",
      risk: "Risiko",
      compliance: "Compliance",
      skills: "Skill",
      evaluation: "Evaluasi",
      workspaces: "Workspaces",
      settings: "Pengaturan",
      platformTenants: "Tenant",
    },
    sidebar: {
      ariaLabel: "Navigasi utama",
      expand: "Buka sidebar",
      collapse: "Ciutkan sidebar",
    },
    userMenu: {
      logout: "Keluar",
      language: "Bahasa",
    },
  },

  providers: {
    page: {
      title: "Penyedia AI",
      description:
        "Kelola kredensial penyedia AI per organisasi dengan model BYOK. Kunci dienkripsi at-rest dan hanya ditampilkan sekali saat dibuat atau dirotasi.",
      sectionTitle: "Penyedia AI",
      sectionDescription:
        "Konfigurasi kunci API per penyedia (BYOK). Kunci dienkripsi at-rest dengan metode kriptografi standar industri; plaintext hanya ditampilkan sekali saat dibuat atau dirotasi. Setiap perubahan dicatat di audit log.",
      readOnlyNotice:
        "Pengelolaan kredensial penyedia AI hanya tersedia untuk admin organisasi. Anda tetap dapat melihat daftar kredensial yang ada di bawah ini.",
    },
    filters: {
      provider: "Penyedia",
      status: "Status",
    },
    table: {
      name: "Nama",
      provider: "Penyedia",
      key: "Kunci",
      status: "Status",
      created: "Dibuat",
      actions: "Aksi",
      emptyFiltered: "Tidak ada kredensial yang cocok dengan filter.",
    },
    status: {
      active: "Aktif",
      disabled: "Tidak aktif",
      revoked: "Dicabut",
    },
    actions: {
      add: "Tambah penyedia",
      rotate: "Rotasi kunci",
      refreshModels: "Segarkan model",
      refreshModelsSuccess:
        "Katalog model untuk “{name}” disegarkan. Buka ulang Playground untuk melihat daftar terbaru.",
      disable: "Nonaktifkan",
      enable: "Aktifkan",
      revoke: "Cabut",
      rowAriaLabel: "Aksi untuk {name}",
    },
    emptyState: {
      title: "Belum ada kredensial penyedia AI",
      description:
        "Monago menggunakan model BYOK — Anda yang membawa kunci API penyedia AI. Setelah ditambahkan, gateway memakai kunci Anda untuk setiap permintaan inference dari organisasi ini, tanpa biaya tambahan dari Monago untuk panggilan upstream.",
      step1Title: "Ambil kunci API dari dashboard penyedia",
      step1Body:
        "OpenAI, Anthropic, atau AWS Bedrock. Buat kunci dengan akses baca dan tulis untuk model yang ingin Anda gunakan.",
      step2Title: "Tambahkan kredensial di Monago",
      step2Body:
        "Klik \"Tambah penyedia\". Monago memvalidasi kunci ke endpoint penyedia sebelum menyimpan. Plaintext dienkripsi at-rest dan ditampilkan sekali untuk Anda salin.",
      step3Title: "Gateway otomatis menggunakan kunci Anda",
      step3Body:
        "Permintaan inference dari organisasi ini diteruskan ke penyedia menggunakan kredensial yang Anda tambahkan. Audit trail tersedia di tab Observability.",
    },
    createDialog: {
      title: "Tambah kredensial penyedia AI",
      description:
        "Monago memvalidasi kunci ke endpoint penyedia sebelum menyimpan. Kunci dienkripsi at-rest dan plaintext hanya ditampilkan sekali — siapkan password manager sebelum melanjutkan.",
      providerLabel: "Penyedia",
      comingSoon: "Segera hadir",
      providerHintBedrock:
        "Catatan: validasi otomatis untuk AWS Bedrock akan tersedia di rilis berikutnya.",
      providerHintDefault:
        "Kunci akan divalidasi terhadap endpoint metadata milik penyedia.",
      nameLabel: "Nama kredensial",
      namePlaceholder: "misalnya production-openai",
      nameError:
        "Hanya huruf, angka, spasi, titik, underscore, dan tanda hubung (1–200 karakter).",
      nameHint:
        "Label internal. Anda dapat membuat beberapa kredensial per penyedia (misalnya 'prod' dan 'staging').",
      keyLabel: "Kunci API",
      keyPlaceholder: "sk-…",
      keyError: "Kunci minimal 8 karakter.",
      keyHint:
        "Diambil dari dashboard penyedia AI Anda. Tidak ditampilkan kembali setelah kredensial dibuat.",
      baseUrlLabel: "Base URL (opsional)",
      baseUrlPlaceholder: "https://gateway.contoh.co.id",
      baseUrlError: "Harus dimulai dengan http:// atau https://.",
      baseUrlHint:
        "Override endpoint bila penyedia AI Anda berada di belakang gateway internal.",
      providerDetail: "Detail penyedia: {detail}",
      cancel: "Batal",
      submit: "Tambah kredensial",
      submitLoading: "Memvalidasi kunci…",
    },
    rotateDialog: {
      title: "Rotasi kunci API",
      description:
        "Setelah rotasi berhasil, kunci lama tidak lagi berfungsi di gateway. Pastikan kunci baru valid sebelum melanjutkan.",
      credentialLabel: "Kredensial",
      currentKeyLabel: "Kunci saat ini:",
      keyLabel: "Kunci API baru",
      keyPlaceholder: "sk-…",
      keyError: "Kunci minimal 8 karakter.",
      keyHint:
        "Ambil dari dashboard penyedia. Ditampilkan sekali setelah rotasi berhasil.",
      providerDetail: "Detail penyedia: {detail}",
      cancel: "Batal",
      submit: "Rotasi kunci",
      submitLoading: "Memvalidasi kunci…",
    },
    revokeDialog: {
      title: "Cabut kredensial?",
      description:
        "Setelah dicabut, gateway tidak lagi menggunakan kredensial ini. Permintaan inference akan menggunakan kunci default platform jika tersedia, atau gagal jika tidak ada fallback.",
      warning:
        "Pencabutan bersifat permanen. Baris kredensial tetap tersimpan untuk jejak audit, dan tidak ada proses undo — buat kredensial baru jika diperlukan.",
      targetLabel: "Kredensial yang akan dicabut",
      last4Label: "Empat karakter terakhir:",
      confirmLabel: "Ketik \"{name}\" untuk konfirmasi",
      confirmHint:
        "Nama harus persis sama (case-sensitive) untuk mengaktifkan tombol cabut.",
      cancel: "Batal",
      submit: "Cabut kredensial",
      submitLoading: "Mencabut…",
    },
    showOnceDialog: {
      headlineCreated: "Kunci API berhasil dibuat",
      headlineRotated: "Kunci API berhasil dirotasi",
      description:
        "Simpan kunci API berikut sekarang. Setelah dialog ditutup, Monago tidak menyimpan plaintext dan tidak dapat menampilkannya kembali.",
      copyAria: "Salin kunci API",
      copiedAria: "Kunci tersalin",
      copy: "Salin",
      copied: "Tersalin",
      warningTitle: "Penting:",
      warningBody:
        "Kunci ini hanya ditampilkan sekali. Simpan di password manager atau secret vault sebelum menutup dialog. Monago menyimpan kunci dalam bentuk terenkripsi di sisi server; plaintext tidak ditulis ke audit log.",
      last4Label: "Empat karakter terakhir:",
      createdLabel: "Dibuat:",
      acknowledge:
        "Saya sudah menyalin kunci API dan menyimpannya di tempat yang aman.",
      close: "Sudah disimpan, tutup",
    },
  },
  workspaces: {
    page: {
      title: "Workspace",
      description:
        "{active} dari {max} workspace aktif. Workspace mengisolasi policy, inventaris aset, dan kunci API per environment atau tim.",
      readOnlyNotice:
        "Pengelolaan workspace, seperti membuat, mengarsipkan, dan mengatur default, hanya tersedia untuk admin organisasi. Anda tetap dapat membuka detail workspace tempat Anda menjadi anggota.",
      limitReached:
        "Batas {max} workspace aktif telah tercapai. Arsipkan workspace yang tidak lagi digunakan sebelum menambahkan workspace baru.",
      sectionTitle: "Semua workspace",
      sectionDescription:
        "Termasuk workspace yang telah diarsipkan. Workspace tersebut disembunyikan dari pemilih workspace, namun tetap tersimpan untuk keperluan audit.",
    },
    table: {
      name: "Nama",
      slug: "Slug",
      status: "Status",
      default: "Default",
      members: "Anggota",
      created: "Dibuat",
      actions: "Aksi",
      empty:
        "Belum ada workspace. Workspace default organisasi dibuat saat proses onboarding. Bila workspace tersebut tidak muncul, hubungi tim Monago.",
    },
    status: {
      active: "Aktif",
      archived: "Diarsipkan",
    },
    roles: {
      workspace_admin: "Admin Workspace",
      workspace_member: "Anggota",
      workspace_viewer: "Pengamat",
    },
    badges: {
      isDefault: "Default",
      defaultYes: "Ya",
      defaultNo: "Tidak",
    },
    actions: {
      create: "Buat workspace",
      openDetail: "Buka detail",
      setDefault: "Jadikan default",
      defaultActive: "Default aktif",
      archive: "Arsipkan",
      rowAriaLabel: "Aksi untuk {name}",
    },
    detail: {
      backToList: "Kembali ke daftar",
      backToWorkspaces: "Kembali ke daftar workspace",
      noDescription: "Tidak ada deskripsi.",
      setDefault: "Jadikan default",
      archive: "Arsipkan",
      metadataTitle: "Metadata",
      metadataDescription:
        "Identitas dasar workspace. Slug dan ID organisasi bersifat permanen demi menjaga keterlacakan data.",
      fieldSlug: "Slug",
      fieldStatus: "Status",
      fieldDefault: "Workspace default",
      fieldMemberCount: "Jumlah anggota",
      fieldCreated: "Dibuat",
      fieldUpdated: "Diperbarui",
      membersTitle: "Anggota workspace",
      membersDescription:
        "Admin organisasi selalu dapat mengelola anggota. Pemilik peran workspace_admin hanya dapat mengelola anggota pada workspace ini.",
      addMember: "Tambah anggota",
      memberName: "Nama",
      memberEmail: "Email",
      memberRole: "Peran",
      memberJoined: "Bergabung",
      memberActions: "Aksi",
      membersEmpty:
        "Belum ada anggota. Pilih \"Tambah anggota\" untuk mulai menambahkan anggota workspace.",
      removeMember: "Keluarkan",
      removeMemberAriaLabel: "Keluarkan {email} dari workspace",
    },
    addModal: {
      title: "Buat workspace baru",
      description:
        "Workspace mengisolasi policy, inventaris aset, dan kunci API per environment. Anggota organisasi Anda dapat ditetapkan sebagai admin, anggota, atau pengamat workspace.",
      nameLabel: "Nama workspace",
      namePlaceholder: "misalnya Production, Staging, Treasury",
      nameError: "Wajib diisi, antara 1 hingga 200 karakter.",
      slugLabel: "Slug",
      slugPlaceholder: "production",
      slugError:
        "Hanya huruf kecil, angka, dan tanda hubung, antara 1 hingga 100 karakter.",
      slugHint:
        "Dibuat otomatis dari nama dan dapat disesuaikan. Digunakan sebagai pengenal pada URL dan basis data.",
      descriptionLabel: "Deskripsi (opsional)",
      descriptionPlaceholder: "Konteks penggunaan workspace ini",
      cancel: "Batal",
      submit: "Buat workspace",
      submitLoading: "Membuat…",
    },
    archiveDialog: {
      title: "Arsipkan workspace?",
      description:
        "Workspace yang diarsipkan tidak lagi tampil di pemilih workspace dan tidak menerima permintaan baru. Riwayat dan audit log tetap tersimpan dengan integritas terjaga.",
      workspaceLabel: "Workspace",
      slugLabel: "Slug:",
      defaultSuffix: "· default",
      confirmLabel: "Ketik \"{name}\" untuk konfirmasi",
      confirmHint:
        "Nama harus persis sama, termasuk huruf besar dan kecil, untuk mengaktifkan tombol arsip.",
      cancel: "Batal",
      submit: "Arsipkan workspace",
      submitLoading: "Mengarsipkan…",
      blockedDefault:
        "Workspace default tidak dapat diarsipkan. Tetapkan workspace lain sebagai default terlebih dahulu.",
      blockedLastActive:
        "Minimal harus ada satu workspace aktif. Buat workspace lain terlebih dahulu.",
    },
    addMemberDialog: {
      title: "Tambah anggota workspace",
      description:
        "Pilih anggota organisasi lalu tetapkan peran workspace. Hanya pengguna aktif yang belum tergabung di workspace ini yang ditampilkan.",
      searchLabel: "Cari pengguna",
      searchPlaceholder: "Nama atau email",
      loadingUsers: "Memuat pengguna…",
      noCandidates:
        "Tidak ada kandidat. Semua anggota organisasi telah bergabung, atau tidak ada yang cocok dengan kata kunci pencarian.",
      roleLabel: "Peran workspace",
      roleGroupAriaLabel: "Peran",
      roleHintAdmin: "Mengelola anggota dan menyunting metadata workspace.",
      roleHintMember:
        "Akses standar untuk penggunaan sehari-hari pada policy dan aset workspace.",
      roleHintViewer:
        "Akses hanya-baca, cocok untuk auditor atau pengamat eksternal.",
      cancel: "Batal",
      submit: "Tambah anggota",
      submitLoading: "Menambahkan…",
    },
    selector: {
      loading: "Memuat workspace…",
      loadingAriaLabel: "Memuat workspace",
      createFirst: "Buat workspace",
      createFirstAriaLabel: "Buat workspace pertama",
      emptyNonAdmin: "Belum ada workspace",
      emptyCompact: "—",
      placeholder: "Pilih workspace",
      triggerAriaLabel: "Workspace: {name}",
      triggerUnselected: "belum dipilih",
      defaultSuffix: "· default",
      createNew: "Buat workspace baru",
      manage: "Kelola workspace",
    },
  },
  policies: {
    page: {
      title: "Kebijakan",
      description:
        "Aturan tata kelola yang dievaluasi terhadap setiap permintaan inference. Setiap kebijakan memiliki riwayat versi — perubahan dibuat sebagai versi baru.",
      breadcrumbLoading: "Memuat…",
      notFound:
        "Kebijakan tidak ditemukan. Kebijakan mungkin telah dihapus, atau Anda tidak memiliki akses ke kebijakan ini.",
      missingId: "Pengenal kebijakan tidak ditemukan pada alamat ini.",
    },
    table: {
      name: "Nama",
      slug: "Slug",
      status: "Status",
      priority: "Prioritas",
      updated: "Pembaruan terakhir",
      actions: "Aksi",
      emptyFiltered:
        "Tidak ada kebijakan yang cocok dengan filter saat ini. Sesuaikan filter untuk menampilkan kebijakan lain.",
    },
    status: {
      enabled: "Aktif",
      disabled: "Nonaktif",
    },
    effects: {
      allow: "Izinkan",
      deny: "Tolak",
      logOnly: "Catat saja",
      redact: "Sunting data sensitif",
      requireApproval: "Perlu persetujuan",
      requireHitl: "Perlu tinjauan manusia",
      escalate: "Eskalasi",
    },
    actions: {
      create: "Buat kebijakan",
      newVersion: "Versi baru",
      delete: "Hapus",
      deleteRowAriaLabel: "Hapus kebijakan {name}",
    },
    detail: {
      decisionsTitle: "Keputusan terbaru",
      decisionsDescription:
        "20 evaluasi terakhir terhadap kebijakan ini.",
      decisionsEmpty:
        "Belum ada keputusan tercatat untuk kebijakan ini. Keputusan akan muncul setelah permintaan inference dievaluasi terhadap kebijakan ini.",
      colTime: "Waktu",
      colEffect: "Hasil",
      colRequestId: "ID permintaan",
      colUser: "Pengguna",
      systemActor: "sistem",
      metadataSlug: "Slug",
      metadataPriority: "Prioritas",
      metadataStatus: "Status",
      metadataUpdated: "Pembaruan terakhir",
    },
    createDialog: {
      title: "Buat kebijakan baru",
      description:
        "Pilih kontrol akses model untuk allowlist cepat, atau mode lanjutan untuk menyunting DSL penuh. Versi pertama otomatis menjadi versi aktif setelah dibuat.",
      modeAria: "Mode pembuatan kebijakan",
      modeModelAccess: "Kontrol akses model",
      modeAdvanced: "Lanjutan (DSL)",
      templateLabel: "Template (opsional)",
      templateNone: "— Buat kustom (tanpa template) —",
      templateError: "Gagal memuat template: {detail}",
      nameLabel: "Nama",
      namePlaceholder: "contoh: Penyuntingan data PII",
      slugLabel: "Slug",
      slugPlaceholder: "pii-redaction",
      slugError: "Hanya huruf kecil, angka, dan tanda hubung.",
      slugHint: "Dibuat otomatis dari nama; dapat disunting manual.",
      descriptionLabel: "Deskripsi (opsional)",
      descriptionPlaceholder: "Penjelasan singkat tentang tujuan kebijakan",
      priorityLabel: "Prioritas",
      priorityHint:
        "0 = paling rendah, 100 = paling tinggi. Nilai bawaan 50.",
      ruleLabel: "Definisi aturan (JSON)",
      ruleHint:
        "Wajib: name · when · effect. Opsional: priority · description · framework_mapping",
      cancel: "Batal",
      submit: "Buat kebijakan",
      submitLoading: "Membuat…",
    },
    modelAccess: {
      intro: "Allowlist model yang diizinkan untuk scope ini.",
      introDetail:
        "Permintaan untuk model lain akan ditolak oleh policy engine. Riwayat audit + keputusan tetap dipertahankan.",
      scopeLabel: "Scope",
      scopeWorkspace: "Workspace ini",
      scopeTenant: "Seluruh tenant",
      scopeNoWorkspaceHint:
        "Anda tidak berada di workspace aktif — scope tenant akan dipakai.",
      modelsLabel: "Model yang diizinkan",
      modelsHint: "{count} model dipilih.",
      modelsRequiredError: "Minimal satu model harus dipilih.",
      searchPlaceholder: "Cari model…",
      selectAll: "Pilih semua",
      selectFiltered: "Pilih {count} hasil",
      clear: "Hapus",
      removeModel: "Hapus {model}",
      searchEmpty: "Tidak ada model yang cocok dengan “{query}”.",
      catalogError: "Gagal memuat katalog model: {detail}",
      catalogEmpty:
        "Belum ada model yang dapat dipanggil — hubungkan provider terlebih dahulu di halaman Providers.",
      denyMessage:
        "Model ini tidak diizinkan oleh kebijakan workspace Anda.",
    },
    deleteDialog: {
      title: "Hapus kebijakan",
      descriptionBefore: "Tindakan ini menghapus semua versi kebijakan ",
      descriptionMiddle: " dan ",
      descriptionIrreversible: "tidak dapat dibatalkan",
      descriptionAfter:
        ". Keputusan yang sudah tercatat di audit log tetap tersimpan.",
      confirmInstruction:
        "Untuk konfirmasi, ketik nama kebijakan persis seperti tertera:",
      confirmLabel: "Konfirmasi nama",
      confirmError: "Nama tidak cocok.",
      cancel: "Batal",
      submit: "Hapus permanen",
      submitLoading: "Menghapus…",
    },
    versionDialog: {
      title: "Versi baru untuk {name}",
      description:
        "Versi baru otomatis menjadi versi aktif setelah dibuat. Versi sebelumnya diarsipkan dan tetap tersimpan untuk jejak audit.",
      ruleLabel: "Definisi aturan (JSON)",
      ruleHint: "Wajib: name · when · effect.",
      changelogLabel: "Catatan perubahan (opsional)",
      changelogPlaceholder:
        "contoh: tambah field NPWP ke daftar penyuntingan",
      changelogHint: "Catatan internal yang melekat pada versi ini.",
      cancel: "Batal",
      submit: "Buat & aktifkan versi",
      submitLoading: "Memproses…",
    },
    quickstart: {
      title: "Belum ada kebijakan",
      description:
        "Kebijakan adalah aturan tata kelola yang dijalankan terhadap setiap permintaan inference. Ikuti tiga langkah berikut untuk memulai.",
      step1Title: "Pilih template BFSI atau buat kustom",
      step1Body:
        "Enam template BFSI siap pakai tersedia di dropdown saat Anda memilih \"Buat kebijakan\" — antara lain pembatasan LLM eksternal untuk finance/treasury, model lokal khusus data HR, dan lainnya. Setiap template sudah menyertakan pemetaan kerangka kepatuhan ke ISO 42001, UU PDP 2022, dan POJK 11/2022. Contoh isi rule_definition:",
      step2Title: "Sesuaikan untuk konteks organisasi Anda",
      step2Body:
        "Setelah template dimuat ke editor, ubah nama, deny_user_message, dan kondisi when sesuai workspace dan tim internal Anda. Slug dibuat otomatis dari nama; field priority (0–100) menentukan urutan evaluasi terhadap aturan lain.",
      step3Title: "Terbitkan — versi pertama otomatis aktif",
      step3Body:
        "Setiap kebijakan dapat memiliki banyak versi; versi pertama langsung aktif setelah berhasil dibuat. Untuk perubahan berikutnya, buat versi baru melalui halaman detail — versi lama diarsipkan untuk keperluan audit dan rollback.",
    },
  },
  assets: {
    page: {
      title: "Inventaris aset",
      description:
        "Basis data terpusat untuk aset AI: prompt, model, agen, alur kerja, penyedia, alat, dan dataset. Setiap aset memiliki klasifikasi risiko dan tahap siklus hidup.",
      breadcrumbLoading: "Memuat…",
      notFound:
        "Aset tidak ditemukan. Aset mungkin telah diarsipkan, atau Anda tidak memiliki akses ke aset ini.",
      missingId: "Pengenal aset tidak ditemukan pada alamat ini.",
    },
    table: {
      name: "Nama",
      type: "Tipe",
      lifecycle: "Siklus hidup",
      risk: "Risiko",
      tags: "Tag",
      updated: "Pembaruan terakhir",
      actions: "Aksi",
      emptyFiltered:
        "Tidak ada aset yang cocok dengan filter saat ini. Sesuaikan filter untuk menampilkan aset lain.",
    },
    status: {
      draft: "Draf",
      review: "Tinjauan",
      active: "Aktif",
      deprecated: "Tidak disarankan",
      retired: "Dipensiunkan",
    },
    types: {
      prompt: "Prompt",
      model: "Model",
      agent: "Agen",
      workflow: "Alur kerja",
      provider: "Penyedia",
      tool: "Alat",
      dataset: "Dataset",
    },
    risk: {
      low: "Rendah",
      medium: "Sedang",
      high: "Tinggi",
      critical: "Kritis",
    },
    actions: {
      create: "Daftarkan aset",
      transition: "Pindahkan tahap",
      delete: "Hapus",
      deleteRowAriaLabel: "Hapus aset {name}",
    },
    detail: {
      fieldType: "Tipe",
      fieldLifecycle: "Siklus hidup",
      fieldRisk: "Risiko",
      fieldUpdated: "Pembaruan terakhir",
      fieldSlug: "Slug",
      fieldTags: "Tag",
      fieldCoOwners: "Pemilik bersama",
      fieldDependencies: "Ketergantungan",
      coOwnersCount: "{count} pengguna",
      dependenciesCount: "{out} keluar · {in} masuk",
      historyTitle: "Riwayat perubahan",
      historyDescription: "20 peristiwa terakhir pada aset ini.",
      historyColTime: "Waktu",
      historyColChangeType: "Jenis perubahan",
      historyColChangedBy: "Diubah oleh",
      historyEmpty:
        "Belum ada perubahan tercatat pada aset ini. Perubahan akan muncul setelah aset disunting atau dipindahkan tahapnya.",
      systemActor: "sistem",
    },
    createDialog: {
      title: "Daftarkan aset baru",
      description:
        "Pilih tipe aset, beri nama, dan tentukan klasifikasi risiko awal. Tahap siklus hidup secara bawaan adalah Draf dan dapat diubah dari halaman detail.",
      typeLabel: "Tipe aset",
      nameLabel: "Nama",
      namePlaceholder: "contoh: {type} produksi",
      slugLabel: "Slug",
      slugHint: "Dibuat otomatis dari nama; dapat disunting manual.",
      descriptionLabel: "Deskripsi (opsional)",
      descriptionPlaceholder: "Penjelasan singkat tentang aset",
      riskLabel: "Klasifikasi risiko",
      justificationLabel: "Justifikasi bisnis (opsional)",
      justificationPlaceholder:
        "Tujuan bisnis di balik pendaftaran aset ini",
      tagsLabel: "Tag (opsional, pisahkan dengan koma)",
      tagsPlaceholder: "pii, retail, kyc",
      tagsHint: "Tag membantu memfilter daftar aset dan mendukung analitik.",
      cancel: "Batal",
      submit: "Daftarkan aset",
      submitLoading: "Mendaftarkan…",
    },
    deleteDialog: {
      title: "Hapus aset",
      descriptionBefore: "Aset ",
      descriptionAfter:
        " akan diarsipkan. Riwayatnya tetap tersimpan untuk jejak audit, tetapi aset tidak lagi muncul pada daftar aset.",
      confirmInstruction: "Untuk konfirmasi, ketik nama aset persis seperti tertera:",
      confirmLabel: "Konfirmasi nama",
      confirmError: "Nama tidak cocok.",
      cancel: "Batal",
      submit: "Hapus aset",
      submitLoading: "Mengarsipkan…",
    },
    transitionDialog: {
      title: "Pindahkan tahap siklus hidup",
      descriptionBefore: "Aset ",
      descriptionMiddle: " saat ini berada pada tahap ",
      descriptionAfter:
        ". Pilih tahap tujuan dan, bila perlu, catat alasannya.",
      targetLabel: "Tahap tujuan",
      reasonLabel: "Alasan (opsional)",
      reasonPlaceholder:
        "contoh: tinjauan selesai, siap untuk produksi",
      reasonHint: "Catatan ini tersimpan pada riwayat perubahan aset.",
      cancel: "Batal",
      submit: "Pindahkan tahap",
      submitLoading: "Memproses…",
    },
    filterBar: {
      type: "Tipe",
      lifecycle: "Siklus hidup",
      risk: "Risiko",
    },
    quickstart: {
      title: "Belum ada aset terdaftar",
      description:
        "Inventaris aset adalah basis data terpusat untuk aset AI: prompt, model, agen, alur kerja, penyedia, alat, dan dataset. Setiap aset memiliki klasifikasi risiko dan tahap siklus hidup.",
      step1Title: "Daftarkan aset pertama",
      step1Body:
        "Pilih tipe aset — prompt, model, agen, alur kerja, penyedia, alat, atau dataset — lalu beri nama dan tentukan klasifikasi risiko awal.",
      step2Title: "Kelola melalui tahap siklus hidup",
      step2Body:
        "Aset bergerak melalui tahap draf, tinjauan, aktif, tidak disarankan, lalu dipensiunkan. Gunakan tombol \"Pindahkan tahap\" pada halaman detail untuk mengubah tahapnya.",
      step3Title: "Pantau perubahan melalui riwayat",
      step3Body:
        "Setiap perubahan — penggantian nama, klasifikasi ulang risiko, atau perpindahan tahap — tercatat pada riwayat perubahan di halaman detail.",
    },
  },
  risk: {
    page: {
      title: "Postur risiko",
      description:
        "Skor risiko AI Governance untuk organisasi Anda — agregat empat dimensi (keamanan, kebijakan, aset, dan kepatuhan) yang dilengkapi bukti sinyal dan keselarasan terhadap framework.",
    },
    tabs: {
      dashboard: "Dasbor",
      snapshots: "Snapshot",
      signals: "Sinyal",
      methodology: "Metodologi",
    },
    bands: {
      critical: "Kritis",
      needsAttention: "Butuh perhatian",
      good: "Baik",
      excellent: "Sangat baik",
    },
    severity: {
      low: "Rendah",
      medium: "Sedang",
      high: "Tinggi",
      critical: "Kritis",
    },
    dimensions: {
      security: "Keamanan",
      policy: "Kebijakan",
      asset: "Aset",
      compliance: "Kepatuhan",
    },
    dashboard: {
      intro:
        "Skor risiko keseluruhan dihitung dari rata-rata tertimbang empat dimensi: keamanan, kebijakan, aset, dan kepatuhan.",
      recompute: "Hitung ulang",
      recomputeLoading: "Memproses…",
      overallScoreLabel: "Skor keseluruhan",
      deltaLabel: "Perubahan dibanding snapshot sebelumnya",
      maturityLabel: "Maturitas NIST AI RMF",
      computedAt: "Dihitung {time}",
      dimensionsTitle: "Dimensi risiko",
      dimensionsDescription:
        "Empat sub-skor yang membentuk skor keseluruhan.",
      dimensionsColDimension: "Dimensi",
      dimensionsColScore: "Skor",
      dimensionsColDelta: "Perubahan",
      dimensionsEmpty:
        "Belum ada dimensi yang dihitung. Jalankan hitung ulang dari tab Dasbor untuk membuat skor pertama.",
      frameworksTitle: "Keselarasan framework",
      frameworksDescription:
        "Skor kesiapan untuk setiap framework yang aktif pada organisasi ini.",
      frameworksColFramework: "Framework",
      frameworksColReadiness: "Skor kesiapan",
      frameworksColMaturity: "Maturitas",
      frameworksColComputed: "Dihitung",
      frameworksEmpty:
        "Belum ada keselarasan framework yang dihitung. Keselarasan akan muncul setelah skor risiko diperbarui.",
    },
    snapshots: {
      colTime: "Waktu",
      colOverall: "Skor keseluruhan",
      colDelta: "Perubahan",
      empty:
        "Belum ada snapshot. Jalankan hitung ulang dari tab Dasbor untuk membuat snapshot pertama.",
    },
    snapshotDetail: {
      title: "Detail snapshot risiko",
      subtitle: "Dihitung {time} · tipe {type}",
      overallScoreLabel: "Skor keseluruhan",
      deltaLabel: "Perubahan dibanding sebelumnya",
      evidenceLabel: "Bukti sinyal",
      evidenceHint:
        "Jumlah sinyal per dimensi yang digunakan saat menghitung agregat.",
      close: "Tutup",
    },
    signals: {
      filterSeverity: "Tingkat keparahan",
      filterDimension: "Dimensi",
      colTime: "Waktu",
      colType: "Tipe",
      colSeverity: "Tingkat keparahan",
      colDimension: "Dimensi",
      colSource: "Sumber",
      colValue: "Nilai",
      empty:
        "Tidak ada sinyal yang cocok dengan filter saat ini. Sesuaikan filter untuk menampilkan sinyal lain.",
    },
    signalDetail: {
      title: "Detail sinyal",
      subtitle: "{type} · {time}",
      severityLabel: "Tingkat keparahan",
      dimensionLabel: "Dimensi terdampak",
      sourceLabel: "Sumber",
      valueLabel: "Nilai sinyal",
      triggeredLabel: "Memicu perhitungan ulang",
      triggeredYes: "Ya",
      triggeredNo: "Tidak",
      snapshotLabel: "Snapshot terkait",
      metadataLabel: "Metadata peristiwa",
      close: "Tutup",
    },
    methodology: {
      weightsTitle: "Bobot dimensi",
      weightsDescription:
        "Total bobot wajib bernilai 1,00. Setiap sub-skor dikalikan bobotnya lalu dijumlahkan menjadi skor keseluruhan.",
      edit: "Ubah metodologi",
      weightSecurity: "Keamanan",
      weightPolicy: "Kebijakan",
      weightAsset: "Aset",
      weightCompliance: "Kepatuhan",
      weightSumLabel: "Total bobot: {sum}",
      weightSumValid: "(valid)",
      weightSumInvalid: "(harus 1,00 agar dapat disimpan)",
      thresholdsTitle: "Ambang batas tingkat skor",
      thresholdsDescription:
        "Skor di bawah ambang \"Kritis\" ditandai Kritis; di bawah ambang \"Perhatian\" ditandai Butuh perhatian; di bawah ambang \"Baik\" ditandai Baik; sama dengan atau di atas ambang \"Baik\" ditandai Sangat baik.",
      thresholdCritical: "Kritis (<)",
      thresholdNeedsAttention: "Butuh perhatian (<)",
      thresholdGood: "Baik (<)",
      thresholdsOrderError:
        "Ambang batas harus naik berurutan: Kritis lebih kecil dari Butuh perhatian, dan Butuh perhatian lebih kecil dari Baik.",
      thresholdsRangeError:
        "Setiap ambang batas harus berada pada rentang 0 hingga 100.",
      cancel: "Batal",
      save: "Simpan metodologi",
      saveLoading: "Menyimpan…",
      readOnlyNotice:
        "Hanya admin organisasi yang dapat mengubah konfigurasi metodologi.",
    },
  },
  compliance: {
    page: {
      title: "Kepatuhan",
      description:
        "Kelola kepatuhan secara mandiri: berlangganan framework, telaah analisis gap, kelola evidence, serta pantau maturitas NIST AI RMF 1.0 dan ISO/IEC 42001:2023.",
    },
    tabs: {
      posture: "Postur",
      frameworks: "Framework",
      gaps: "Analisis gap",
      evidence: "Evidence",
      maturity: "Maturitas",
    },
    posture: {
      empty:
        "Belum ada framework yang dilanggan. Buka tab \"Framework\" untuk berlangganan framework yang relevan, misalnya NIST AI RMF 1.0 atau ISO/IEC 42001:2023.",
      cardFramework: "Framework",
      readinessLabel: "Skor kesiapan",
      coveredClauses: "Klausa tercakup",
      criticalClauses: "Klausa kritikal",
      gapNotice:
        "{count} klausa belum memiliki evidence — telaah tab \"Analisis gap\" untuk menindaklanjuti.",
    },
    frameworks: {
      intro:
        "Daftar framework yang dilanggan organisasi ini. Setiap framework memicu perhitungan kesiapan dan maturitas secara berkala.",
      colFramework: "Framework",
      colStatus: "Status",
      colTargetCertification: "Target sertifikasi",
      colSince: "Sejak",
      colActions: "Aksi",
      statusActive: "Aktif",
      statusInactive: "Non-aktif",
      empty:
        "Belum ada langganan framework. Pilih \"Berlangganan framework\" untuk memulai pelacakan kesiapan.",
    },
    gaps: {
      frameworkLabel: "Framework",
      noActiveFramework: "— Tidak ada framework aktif —",
      gapCount: "{count} klausa belum memiliki evidence",
      colClauseCode: "Kode klausa",
      colTitle: "Judul",
      colCategory: "Kategori",
      colPriority: "Prioritas",
      noFramework:
        "Belum ada framework yang dilanggan. Berlangganan framework terlebih dahulu pada tab \"Framework\".",
      noGaps:
        "Tidak ada gap — seluruh klausa sudah memiliki evidence.",
      note:
        "Catatan: klausa berprioritas \"Kritikal\" perlu ditangani lebih dahulu agar skor kesiapan terangkat lebih cepat.",
    },
    evidence: {
      intro:
        "Evidence mengaitkan kebijakan, aset, atau snapshot risiko dengan klausa framework. Pemetaan otomatis dijalankan oleh sistem; admin organisasi dapat menambahkan evidence manual atau mencabut evidence yang sudah tidak berlaku.",
      typeFilterLabel: "Tipe",
      includeRevoked: "Tampilkan evidence yang sudah dicabut",
      colClause: "Klausa",
      colType: "Tipe",
      colReference: "Referensi",
      colCreated: "Dibuat",
      colStatus: "Status",
      colActions: "Aksi",
      statusActive: "Aktif",
      statusRevoked: "Dicabut",
      empty:
        "Belum ada evidence yang cocok dengan filter saat ini. Sesuaikan filter atau tambahkan evidence baru.",
    },
    maturity: {
      nistTitle: "NIST AI RMF 1.0",
      nistDescription:
        "Tingkat maturitas per fungsi inti: GOVERN, MAP, MEASURE, dan MANAGE.",
      isoTitle: "ISO/IEC 42001:2023",
      isoDescription:
        "Tingkat maturitas per fase siklus: PLAN, DO, CHECK, dan ACT.",
      overallLabel: "Maturitas keseluruhan",
      functionLabel: "Fungsi",
      phaseLabel: "Fase",
      coverage: "{pct}% cakupan",
    },
    priority: {
      critical: "Kritikal",
      high: "Tinggi",
      normal: "Normal",
    },
    evidenceType: {
      policy_version: "Versi kebijakan",
      asset: "Aset",
      risk_snapshot: "Snapshot risiko",
      audit_log: "Log audit",
      framework_alignment: "Keselarasan framework",
    },
    actions: {
      subscribe: "Berlangganan framework",
      unsubscribe: "Berhenti",
      submitEvidence: "Kirim evidence",
      revokeEvidence: "Cabut",
    },
    subscribeDialog: {
      title: "Berlangganan framework",
      description:
        "Aktifkan pelacakan kesiapan untuk framework tambahan. Sistem akan menghitung skor dan mengaitkan evidence secara otomatis dari kebijakan serta aset yang sudah ada.",
      frameworkLabel: "Framework",
      frameworkPlaceholder: "— Pilih framework —",
      allSubscribed:
        "Seluruh framework yang tersedia sudah dilanggan.",
      targetDateLabel: "Target sertifikasi (opsional)",
      targetDateHint:
        "Tanggal target sebagai acuan roadmap audit internal.",
      cancel: "Batal",
      submit: "Berlangganan",
      submitLoading: "Memproses…",
    },
    evidenceSubmitDialog: {
      title: "Kirim evidence manual",
      description:
        "Kaitkan versi kebijakan, aset, snapshot risiko, atau log audit dengan klausa framework tertentu untuk menutup gap. Tindakan ini dapat dibatalkan dengan mencabut evidence di kemudian hari.",
      clauseIdLabel: "ID klausa",
      clauseIdPlaceholder: "ID klausa — lihat tab \"Analisis gap\"",
      typeLabel: "Tipe evidence",
      referenceIdLabel: "ID referensi",
      referenceIdPlaceholder:
        "ID resource, misalnya ID versi kebijakan",
      referenceIdHint:
        "ID dari resource yang digunakan sebagai evidence.",
      expiresAtLabel: "Berlaku sampai (opsional)",
      expiresAtHint:
        "Kosongkan bila evidence tidak memiliki masa berlaku.",
      cancel: "Batal",
      submit: "Kirim evidence",
      submitLoading: "Menyimpan…",
    },
  },
  observability: {
    page: {
      title: "Observabilitas",
      description:
        "Pantau postur tata kelola, metrik inferensi, keputusan kebijakan, dan audit trail organisasi Anda.",
    },
    tabs: {
      overview: "Ringkasan",
      inference: "Inferensi",
      policy: "Kebijakan",
      audit: "Log audit",
    },
    overview: {
      tilePostureLabel: "Postur tata kelola",
      tilePostureNoData: "Tidak ada data",
      tileRequestsLabel: "Permintaan 24 jam",
      tileRequestsP99: "p99 {value}",
      tileRequestsNoData: "Belum ada data",
      tileDecisionsLabel: "Keputusan kebijakan (7 hari)",
      tileDecisionsDenied: "{count} ditolak",
      tileAuditLabel: "Peristiwa audit terbaru",
      tileAuditCaption: "Buka tab Audit untuk detail",
      concernsTitle: "Konsentrasi risiko",
      concernsDescription:
        "Perhatian utama dari laporan postur tata kelola.",
      concernsEmpty:
        "Belum ada perhatian yang dilaporkan. Postur tata kelola organisasi Anda berada dalam kondisi baik untuk saat ini.",
      recentAuditTitle: "Aktivitas audit terbaru",
      recentAuditDescription:
        "Lima peristiwa terakhir pada organisasi Anda.",
      recentAuditEmpty:
        "Belum ada peristiwa audit. Aktivitas akan muncul di sini setelah permintaan pertama melewati gateway.",
    },
    inference: {
      intro:
        "Metrik latensi menyeluruh beserta rincian per tahap gateway.",
      tileP50: "p50",
      tileP95: "p95",
      tileP99: "p99",
      tileTotal: "Total",
      stagesTitle: "Latensi per tahap gateway",
      stagesDescription:
        "Autentikasi, pemindaian PII, evaluasi kebijakan, panggilan ke penyedia, dan pencatatan audit.",
      stagesColStage: "Tahap",
      stagesColP50: "p50",
      stagesColP95: "p95",
      stagesColP99: "p99",
      stagesColCount: "Jumlah",
      stagesEmpty:
        "Belum ada data per tahap untuk rentang waktu ini. Coba pilih rentang waktu yang lebih panjang.",
      stagesGroupGateway: "Gateway (governance)",
      stagesGroupProvider: "Provider (LLM)",
      stagesGroupOther: "Lainnya",
      topModelsTitle: "Model teratas",
      topModelsDescription:
        "Model yang paling sering dipanggil dalam rentang waktu ini.",
      topModelsColModel: "Model",
      topModelsColRequests: "Permintaan",
      topModelsColTokens: "Token",
      topModelsColCostUsd: "Biaya (USD)",
      topModelsColCostIdr: "Biaya (IDR)",
      topModelsColAvgLatency: "Rata-rata latensi",
      topModelsEmpty:
        "Belum ada panggilan model untuk rentang waktu ini. Coba pilih rentang waktu yang lebih panjang.",
      costTitle: "Estimasi biaya (informasi)",
      costDescription:
        "Estimasi total biaya pada rentang waktu ini. Token ditagihkan langsung oleh provider Anda; nilai IDR adalah konversi dengan kurs tenant, untuk pelaporan saja.",
      costTileTotalUsd: "Total (USD)",
      costTileTotalIdr: "Total (IDR)",
      costTileFxRate: "Kurs",
      costFxCaption: "@ Rp {{rate}} / USD",
      costPricingSourceLabel: "Sumber harga",
      costPricingFallbackBadge: "Estimasi (fallback)",
      costEmpty:
        "Belum ada data biaya untuk rentang waktu ini. Coba pilih rentang waktu yang lebih panjang.",
    },
    policy: {
      intro:
        "Statistik keputusan policy engine beserta kebijakan yang paling sering dipicu.",
      totalLabel: "Total keputusan",
      breakdownTitle: "Rincian per effect",
      breakdownDescription:
        "Distribusi lengkap seluruh effect dalam rentang waktu ini.",
      breakdownColEffect: "Effect",
      breakdownColCount: "Jumlah",
      breakdownColPercentage: "Persentase",
      breakdownEmpty:
        "Belum ada keputusan tercatat untuk rentang waktu ini.",
      topTitle: "Kebijakan paling sering dipicu",
      topDescription:
        "Sepuluh kebijakan yang paling aktif dalam rentang waktu ini.",
      topColName: "Nama kebijakan",
      topColHits: "Pemicuan",
      topColEffect: "Effect",
      topEmpty:
        "Belum ada kebijakan yang dipicu untuk rentang waktu ini.",
    },
    audit: {
      filterStatusAria: "Saring berdasarkan status",
      filterStatusAll: "Semua status",
      filterPiiAria: "Saring berdasarkan PII",
      filterPiiAll: "Semua PII",
      filterPiiDetected: "PII terdeteksi",
      filterPiiNone: "Tanpa PII",
      filterActionAria: "Saring berdasarkan aksi",
      filterActionPlaceholder: "Aksi (misalnya api_key.create)",
      reset: "Atur ulang",
      colTime: "Waktu",
      colAction: "Aksi",
      colStatus: "Status",
      colPath: "Path",
      colLatency: "Latensi",
      colPii: "PII",
      latencyValue: "{ms} ms",
      piiPresent: "Ya",
      emptyFiltered:
        "Tidak ada peristiwa audit yang cocok dengan filter. Sesuaikan filter untuk menampilkan peristiwa lain.",
      empty:
        "Belum ada peristiwa audit pada organisasi ini. Aktivitas akan tercatat di sini setelah permintaan pertama melewati gateway.",
    },
    auditStatus: {
      success: "Berhasil",
      blocked: "Diblokir",
      error: "Gagal",
    },
    effects: {
      allow: "Izinkan",
      deny: "Tolak",
      require_hitl: "Tinjauan manusia",
      require_approval: "Persetujuan",
      redact: "Penyamaran",
      log_only: "Catat saja",
      escalate: "Eskalasi",
    },
    windows: {
      "1h": "1 jam",
      "24h": "24 jam",
      "7d": "7 hari",
      "30d": "30 hari",
    },
  },
  settings: {
    page: {
      title: "Pengaturan",
      description:
        "Atur profil pengguna, metadata organisasi, dan anggota organisasi dari satu tempat.",
      tabProfileLabel: "Profil pengguna",
      tabProfileDescription: "Identitas dan preferensi",
      tabTenantLabel: "Organisasi",
      tabTenantDescription: "Nama dan kontak organisasi",
      tabUsersLabel: "Pengguna & undangan",
      tabUsersDescription: "Anggota dan kode undangan",
      tabFxRateLabel: "Kurs USD → IDR",
      tabFxRateDescription: "Kurs untuk pelaporan biaya",
      tabAllowedOriginsLabel: "Origin yang diizinkan",
      tabAllowedOriginsDescription: "Origin browser yang diizinkan via CORS",
      tabDataProtectionLabel: "Data Protection",
      tabDataProtectionDescription: "Aksi untuk PII dan prompt-injection",
    },
    roles: {
      super_admin: "Super admin",
      admin: "Admin",
      member: "Anggota",
      viewer: "Pengamat",
      partner_admin: "Partner admin",
    },
    userStatus: {
      active: "Aktif",
      suspended: "Ditangguhkan",
      deleted: "Dihapus",
    },
    profile: {
      identity: {
        title: "Identitas",
        description:
          "Detail akun ini diambil dari sesi login dan tidak dapat diubah langsung dari halaman ini.",
        emptyValue: "—",
        roleLabel: "Peran",
        tenantIdLabel: "ID organisasi",
        workspaceLabel: "Workspace aktif",
      },
      password: {
        title: "Ubah kata sandi",
        description:
          "Tautan untuk mengatur ulang kata sandi dikirim ke email Anda. Perubahan kata sandi tidak dilakukan langsung di antarmuka agar setiap perubahan tercatat dan tidak membuka celah penebakan akun.",
        intro:
          "Klik tombol di bawah untuk menerima email berisi tautan pengaturan ulang kata sandi. Sesi Anda saat ini tetap aktif; kata sandi baru berlaku setelah Anda membuka tautan dari email.",
        sentNotice:
          "Bila email {email} terdaftar, tautan pengaturan ulang kata sandi telah dikirim. Periksa kotak masuk Anda — tautan ini berlaku untuk waktu singkat.",
        sendButton: "Kirim email pengaturan ulang kata sandi",
        sendButtonLoading: "Mengirim...",
        resendButton: "Kirim ulang",
        resendAria: "Kirim ulang permintaan pengaturan ulang kata sandi",
      },
      language: {
        title: "Preferensi bahasa",
        description:
          "Bahasa antarmuka. Preferensi ini disimpan di peramban ini saja — sinkronisasi dengan server akan tersedia pada rilis berikutnya.",
        groupAria: "Bahasa antarmuka",
        idLabel: "Bahasa Indonesia",
        idDescription:
          "Bawaan — seluruh teks dan format tanggal mengikuti standar Indonesia.",
        enLabel: "English",
        enDescription:
          "Eksperimental — sebagian teks masih dalam Bahasa Indonesia dan belum diterjemahkan.",
      },
    },
    tenant: {
      readOnlyNotice:
        "Hanya admin organisasi yang dapat mengubah pengaturan ini. Anda tetap dapat melihat metadata di bawah ini.",
      savedNotice:
        "Perubahan disimpan {time}. Catatan audit menyimpan riwayat siapa mengubah apa.",
      identity: {
        title: "Identitas organisasi",
        description:
          "Slug dan ID bersifat permanen demi keterlacakan data. Nama tampilan dan email kontak dapat diubah oleh admin.",
        nameLabel: "Nama tampilan",
        nameError: "Wajib diisi (1–200 karakter).",
        nameHint:
          "Nama yang muncul di halaman dashboard dan email organisasi.",
        emailLabel: "Email kontak",
        emailError: "Format email tidak valid.",
        emailHint:
          "Opsional. Digunakan untuk notifikasi organisasi dan keperluan penagihan internal.",
        emailPlaceholder: "kontak@perusahaan.co.id",
        tenantIdLabel: "ID organisasi",
        slugLabel: "Slug (tidak dapat diubah)",
        resetButton: "Atur ulang",
        saveButton: "Simpan perubahan",
        saveButtonLoading: "Menyimpan...",
      },
      lifecycle: {
        title: "Status & masa aktif",
        description:
          "Status berlangganan dan waktu pembuatan organisasi. Field ini ditentukan oleh tim Monago dan tidak dapat diubah dari sini.",
        statusLabel: "Status",
        statusActive: "Aktif",
        statusInactive: "Non-aktif",
        createdLabel: "Dibuat",
        updatedLabel: "Diperbarui",
        emptyValue: "—",
      },
    },
    fxRate: {
      readOnlyNotice:
        "Hanya admin organisasi yang dapat mengubah kurs USD → IDR. Pengguna lain melihat kurs dalam mode baca saja.",
      savedNotice:
        "Kurs tersimpan {time}. Tampilan biaya berikutnya menggunakan kurs baru.",
      governanceCaption:
        "Untuk pelaporan biaya saja. Token ditagihkan langsung oleh provider Anda — Monago tidak menagih dan tidak mengambil margin.",
      section: {
        title: "Kurs USD → IDR",
        description:
          "Kurs yang dipakai untuk mengonversi biaya provider (USD) menjadi IDR di dasbor observability. Atur sesuai referensi treasury Anda; telemetri lama tidak dihitung ulang.",
        currentLabel: "Kurs saat ini",
        platformDefault: "Default platform",
        tenantCustom: "Kurs khusus tenant",
        inputLabel: "Kurs baru (Rp / USD)",
        inputHint:
          "Antara Rp 1.000 dan Rp 100.000 per USD. Gunakan rupiah utuh atau maksimal dua desimal.",
        inputError: "Masukkan angka positif antara 1.000 dan 100.000.",
        reasonLabel: "Alasan (opsional)",
        reasonHint:
          "Akan dicatat di audit trail. Berguna untuk review treasury atau finance.",
        reasonPlaceholder: "mis. Referensi treasury 2026-05",
        resetButton: "Atur ulang",
        saveButton: "Simpan kurs",
        saveButtonLoading: "Menyimpan...",
      },
    },
    allowedOrigins: {
      intro:
        "Daftarkan origin browser yang akan memanggil gateway dari web app Anda. Preflight CORS hanya mengizinkan permintaan dari daftar ini ditambah origin sistem Monago.",
      readOnlyNotice:
        "Hanya admin organisasi yang dapat mengubah daftar origin.",
      empty:
        "Belum ada origin kustom. Web app browser yang memanggil gateway harus didaftarkan di sini sebelum preflight CORS-nya berhasil.",
      removeButton: "Hapus",
      removeAria: "Hapus {origin}",
      removeConfirm:
        "Hapus {origin}? Web app yang memakai origin ini akan langsung diblokir.",
      conflictMessage:
        "Origin {origin} sudah terdaftar (oleh tenant lain atau di daftar sistem Monago). Gunakan host yang berbeda.",
      section: {
        title: "Origin browser yang diizinkan",
        description:
          "Setiap entri berbentuk scheme://host[:port] — tanpa path, tanpa wildcard. Produksi wajib https://; localhost diizinkan untuk pengembangan.",
        inputLabel: "Origin baru",
        inputPlaceholder: "https://app.example.com",
        inputHint:
          "Contoh: https://app.example.com, https://acme.id, http://localhost:3000.",
        addButton: "Tambah origin",
        addButtonLoading: "Menambahkan...",
      },
      errors: {
        empty: "Origin tidak boleh kosong.",
        wildcard: "Wildcard origin (*) tidak diizinkan.",
        whitespace: "Origin tidak boleh mengandung spasi.",
        format: "Harus URL yang valid: scheme://host[:port].",
        scheme: "Hanya http:// dan https:// yang didukung.",
        path: "Origin tidak boleh mengandung path.",
        query: "Origin tidak boleh mengandung query atau fragment.",
        userinfo: "Origin tidak boleh mengandung userinfo (user:password@…).",
        https:
          "Origin produksi wajib menggunakan https:// (localhost dikecualikan).",
        duplicate: "Origin sudah ada di daftar.",
      },
    },
    dataProtection: {
      overrideNotice:
        "Kebijakan proteksi default. Policy workspace atau user dapat menimpa pengaturan ini — editor ini hanya mengubah default tenant.",
      firstSaveNotice:
        "Belum ada policy default untuk tenant ini. Simpan pertama kali akan membuatnya dengan nilai di bawah.",
      readOnlyNotice:
        "Hanya admin organisasi yang dapat mengubah pengaturan proteksi.",
      savedNotice:
        "Tersimpan {time}. Request baru akan memakai enforcement terbaru.",
      resetButton: "Atur ulang",
      saveButton: "Simpan",
      saveButtonLoading: "Menyimpan...",
      pii: {
        title: "Penanganan PII",
        description:
          "Tindakan gateway saat detektor PII menemukan data sensitif. Deteksi selalu berjalan; pilihan ini menentukan aksinya.",
        options: {
          block: {
            label: "Block",
            description:
              "Tolak request sebelum dikirim ke provider. Tercatat di audit chain. Mode paling ketat.",
          },
          full_mask: {
            label: "Mask Penuh (Zero-tail)",
            description:
              "Ganti PII jadi [REDACTED_<type>] — tanpa sisa digit. Masking paling ketat.",
          },
          partial_mask: {
            label: "Mask Sebagian (PCI-style)",
            description:
              "Ganti PII tapi sisakan beberapa karakter terakhir (mis. ****-****-****-0123).",
          },
          warn: {
            label: "Warn",
            description:
              "Lanjutkan request apa adanya dan catat insiden di audit log. Mode monitor.",
          },
          allow: {
            label: "Allow",
            description:
              "Lanjutkan apa adanya tanpa insiden. Pakai hanya jika PII memang diharapkan (mis. tool KYC internal).",
          },
        },
      },
      injection: {
        title: "Prompt injection",
        description:
          "Tindakan gateway saat klasifikasi prompt-injection melewati ambang batas.",
        thresholdLabel: "Ambang kepercayaan",
        thresholdHint:
          "Rentang 0,00 – 1,00. Lebih kecil = lebih sensitif (lebih banyak flag). Default 0,70.",
        thresholdError: "Masukkan angka antara 0,00 dan 1,00.",
        options: {
          block: {
            label: "Block",
            description:
              "Tolak request saat klasifikasi melewati ambang batas. Paling ketat.",
          },
          warn: {
            label: "Warn (default)",
            description:
              "Lanjutkan request dan catat insiden — muncul di observability.",
          },
          allow: {
            label: "Allow",
            description:
              "Lewati enforcement sepenuhnya. Detektor tetap berjalan; hasilnya tidak diblok.",
          },
        },
      },
      badges: {
        aria: "Mode enforcement saat ini",
        enforced: "Enforced",
        masking: "Masking",
        monitoring: "Monitoring",
        off: "Off",
      },
    },
    users: {
      readOnlyNotice:
        "Pengelolaan pengguna hanya tersedia untuk admin organisasi. Anda tetap dapat melihat daftar anggota di bawah ini.",
      list: {
        title: "Anggota organisasi",
        descriptionManage:
          "Daftar pengguna aktif. Pengelolaan peran dan penangguhan akun belum tersedia di antarmuka — gunakan API admin atau hubungi tim Monago.",
        descriptionReadOnly:
          "Daftar pengguna aktif pada organisasi ini.",
        filterStatusLabel: "Status",
        filterRoleLabel: "Peran",
        colName: "Nama",
        colEmail: "Email",
        colRole: "Peran",
        colStatus: "Status",
        colLastLogin: "Login terakhir",
        emptyFiltered: "Belum ada anggota yang cocok dengan filter.",
        emptyValue: "—",
        neverLoggedIn: "Belum pernah",
      },
      invites: {
        title: "Undangan aktif",
        description:
          "Kode undangan yang belum digunakan dan belum dicabut. Bagikan kode kepada calon anggota tim — setiap kode hanya dapat digunakan satu kali.",
        inviteButton: "Undang anggota",
        quotaUsage:
          "Menggunakan {active} dari {max} undangan pilot.",
        quotaRemaining: " Tersisa {remaining} slot.",
        quotaReachedBadge: "Batas tercapai",
        quotaReachedAlert:
          "Batas undangan pilot telah tercapai. Cabut undangan yang tidak digunakan atau tunggu hingga kedaluwarsa untuk menambah anggota baru.",
        colPrefix: "Prefiks kode",
        colRole: "Peran",
        colNote: "Catatan",
        colExpires: "Berlaku sampai",
        colActions: "Aksi",
        emptyValue: "—",
        emptyList:
          "Belum ada undangan aktif. Klik \"Undang anggota\" untuk membuat kode undangan baru.",
        revokeButton: "Cabut",
        revokeAria: "Cabut kode undangan {prefix}",
      },
    },
  },
  dashboard: {
    greeting: {
      morning: "Selamat pagi",
      afternoon: "Selamat siang",
      evening: "Selamat sore",
      night: "Selamat malam",
    },
    quickActions: {
      comingSoon: "Akan tersedia pada rilis berikutnya",
      createKeyLabel: "Buat API key",
      createKeyDescription: "Untuk integrasi inference dan governance",
      observabilityLabel: "Lihat observability",
      observabilityDescription:
        "Latensi, penggunaan token, dan integritas audit",
      docsLabel: "Baca dokumentasi",
      docsDescription: "Referensi API dan panduan governance",
    },
    governance: {
      label: "Posture governance",
      noData: "Belum ada data",
      bandExcellent: "Sangat baik",
      bandGood: "Baik",
      bandNeedsAttention: "Perlu perhatian",
      bandCritical: "Perlu tinjauan segera",
      bandHealthy: "Sehat",
      bandApproachingThreshold: "Mendekati ambang batas",
    },
    workspaces: {
      label: "Workspace aktif",
      none: "Tidak ada workspace aktif",
      single: "1 workspace",
      multiple: "{count} workspace",
    },
    requests: {
      label: "Permintaan · 24 jam",
      caption: "Total inference dalam 24 jam terakhir",
    },
    policyDecisions: {
      label: "Keputusan kebijakan · 24 jam",
      caption: "Gabungan izinkan, tolak, persetujuan, dan tinjauan manual",
    },
    riskScore: {
      label: "Skor risiko",
      noData: "Belum ada data",
      bandExcellent: "Sangat baik",
      bandGood: "Baik",
      bandNeedsAttention: "Perlu perhatian",
      bandCritical: "Perlu tinjauan segera",
    },
    recentActivity: {
      label: "Aktivitas terbaru",
      empty: "Belum ada aktivitas dalam 24 jam terakhir.",
    },
  },
  apiKeys: {
    page: {
      title: "API key",
      description:
        "Kelola API key untuk integrasi sistem dengan Monago. Secret key hanya ditampilkan satu kali setelah dibuat.",
      createButton: "Buat API key",
    },
    access: {
      sectionTitle: "Cara mengakses API",
      sectionDescription:
        "Endpoint gateway untuk aplikasi developer. Ganti YOUR_MONAGO_KEY dengan secret key dari tabel di bawah.",
      baseUrlLabel: "Gateway base URL",
      baseUrlNote:
        "Lingkungan produksi menggunakan URL yang berbeda — hubungi tim Monago untuk endpoint final.",
      exampleOpenai: "Contoh permintaan — OpenAI",
      exampleAnthropic: "Contoh permintaan — Anthropic",
      tipLabel: "Tips:",
      tipPrefix: "Konfigurasikan kredensial penyedia AI di ",
      tipLinkLabel: "Penyedia AI",
      tipSuffix:
        " terlebih dahulu. Tanpa kredensial BYOK, gateway dapat menggunakan kunci platform bawaan bila tersedia; jika tidak, permintaan akan ditolak.",
    },
    quickstart: {
      title: "Belum ada API key",
      description:
        "API key adalah kredensial untuk integrasi sistem dengan Monago. Ikuti tiga langkah di bawah ini untuk memulai.",
      step1Title: "Buat API key",
      step1Body:
        "Beri nama yang deskriptif, misalnya \"Production backend\", dan atur batas laju sesuai kebutuhan.",
      step1Button: "Buat API key",
      step2Title: "Uji dengan curl",
      step2Body:
        "Setelah key dibuat, ganti placeholder dan jalankan perintah berikut untuk memvalidasi integrasi:",
      step3Title: "Integrasikan ke sistem Anda",
      step3Body:
        "Simpan API key di variabel lingkungan atau secret vault — jangan menuliskannya langsung di kode aplikasi. Pantau penggunaannya pada tab Observability.",
    },
    filter: {
      all: "Semua",
      active: "Aktif",
      disabled: "Tidak aktif",
      revoked: "Dicabut",
      expired: "Kedaluwarsa",
    },
    status: {
      active: "Aktif",
      disabled: "Tidak aktif",
      revoked: "Dicabut",
      expired: "Kedaluwarsa",
    },
    table: {
      name: "Nama",
      key: "Key",
      status: "Status",
      rateLimit: "Batas laju",
      lastUsed: "Terakhir digunakan",
      actions: "Aksi",
      neverUsed: "Belum pernah digunakan",
      emptyFiltered: "Tidak ada key yang cocok dengan filter saat ini.",
      ratePerMinute: "{count}/menit",
    },
    actions: {
      rowAriaLabel: "Aksi untuk {name}",
      edit: "Ubah nama atau deskripsi",
      enable: "Aktifkan kembali",
      enabling: "Mengaktifkan...",
      disable: "Nonaktifkan",
      revoke: "Cabut permanen",
    },
    createDialog: {
      title: "Buat API key baru",
      description:
        "Secret key hanya ditampilkan satu kali setelah dibuat — simpan dengan aman sebelum menutup dialog.",
      nameLabel: "Nama",
      namePlaceholder: "misalnya: Production backend",
      descriptionLabel: "Deskripsi (opsional)",
      descriptionPlaceholder: "Catatan untuk tim integrasi",
      rateLimitLabel: "Batas laju (per menit)",
      rateLimitHint:
        "Rentang umum: 60–1000 per menit untuk lingkungan produksi.",
      expiryToggle: "Tetapkan tanggal kedaluwarsa",
      expiryUnit: "hari dari sekarang",
      cancel: "Batal",
      submit: "Buat key",
      submitting: "Membuat...",
    },
    editDialog: {
      title: "Ubah API key",
      description:
        "Hanya nama dan deskripsi yang dapat diubah. Untuk mengganti batas laju atau workspace, cabut key ini lalu buat key baru.",
      nameLabel: "Nama",
      descriptionLabel: "Deskripsi",
      descriptionPlaceholder: "Catatan internal (opsional)",
      cancel: "Batal",
      submit: "Simpan",
      submitting: "Menyimpan...",
    },
    disableDialog: {
      title: "Nonaktifkan API key",
      description:
        "Key {name} akan ditolak saat autentikasi sampai diaktifkan kembali. Tindakan ini dapat dibatalkan.",
      reasonLabel: "Alasan (opsional)",
      reasonPlaceholder: "misalnya: rotasi terjadwal kuartal kedua",
      reasonHint:
        "Alasan dicatat di audit log untuk keperluan penelusuran.",
      cancel: "Batal",
      submit: "Nonaktifkan",
      submitting: "Memproses...",
    },
    revokeDialog: {
      title: "Cabut API key",
      description:
        "Tindakan ini tidak dapat dibatalkan. Setelah dicabut, sistem yang masih menggunakan key ini akan gagal autentikasi sampai diganti dengan key baru.",
      confirmPrompt: "Untuk konfirmasi, ketik nama key persis seperti ini: ",
      confirmLabel: "Konfirmasi nama",
      confirmMismatch: "Nama tidak cocok",
      reasonLabel: "Alasan (opsional)",
      reasonPlaceholder: "misalnya: dugaan kebocoran key",
      cancel: "Batal",
      submit: "Cabut permanen",
      submitting: "Mencabut...",
    },
    showOnceDialog: {
      title: "API key berhasil dibuat",
      description:
        "Simpan key ini sekarang. Setelah dialog ditutup, key tidak akan ditampilkan lagi.",
      copy: "Salin",
      copied: "Tersalin",
      warningLabel: "Penting:",
      warningBody:
        "Secret key ini tidak akan ditampilkan lagi. Simpan di password manager atau secret vault sebelum menutup dialog.",
      rateLimitLabel: "Batas laju:",
      rateLimitValue: "{count}/menit",
      expiresLabel: "Kedaluwarsa:",
      acknowledge: "Saya sudah menyimpan secret key di tempat yang aman.",
      close: "Sudah disimpan, tutup",
    },
  },
  playground: {
    page: {
      title: "Playground",
      description:
        "Uji prompt ke model yang terhubung dengan pipeline tata kelola penuh — redaksi PII, penegakan kebijakan, dan jejak audit — diterapkan ke setiap panggilan.",
      noSessionSelected: "Belum ada sesi yang dipilih",
      startSession: "Mulai sesi",
    },
    sessions: {
      title: "Sesi",
      newButtonLabel: "Sesi baru (⌘N)",
      searchPlaceholder: "Cari sesi",
      searchAriaLabel: "Cari sesi",
      copyIdLabel: "Salin id sesi",
      deleteAriaLabel: "Hapus sesi",
      confirmDelete: "Hapus sesi ini?",
      buckets: {
        today: "Hari ini",
        yesterday: "Kemarin",
        earlier: "Sebelumnya",
      },
      empty: {
        title: "Belum ada sesi.",
        cta: "Mulai sesi pertama",
      },
      pills: {
        deny: "DENY",
        pii: "PII",
      },
      time: {
        justNow: "baru saja",
        minutesAgo: "{n}m lalu",
        hoursAgo: "{n}j lalu",
        daysAgo: "{n}h lalu",
      },
    },
    chat: {
      compose: {
        placeholder: "Lanjutkan percakapan…",
        ariaLabel: "Tulis pesan",
        disclaimer: "PII otomatis diredaksi, kebijakan diberlakukan sebelum dikirim.",
        send: "Kirim via Gateway",
        stop: "Hentikan",
      },
      skill: {
        freeform: "Tanpa skill",
        freeformLabel: "Tanpa skill (freeform)",
        activeTitle: "Skill aktif — system prompt dikunci",
        selectTitle: "Pilih skill atau kelola",
        manage: "Kelola skill…",
      },
      meta: {
        msgs: "pesan {n}",
        tokens: "token {n}",
        flags: "flag {n}",
        modelSwitchTitle: "Ganti model",
        modelNoProvidersTitle: "Hubungkan penyedia untuk memilih model",
        modelPickPlaceholder: "Pilih model",
        modelSearchPlaceholder: "Cari model…",
        modelNoResults: "Tidak ada model yang cocok",
        modelPageInfo: "{a}–{b} dari {n}",
        modelPrevPage: "Halaman sebelumnya",
        modelNextPage: "Halaman berikutnya",
        pricePerM: "≈ Rp {in} masuk · Rp {out} keluar / 1jt token",
        noPrice: "estimasi belum tersedia",
        noPriceHint:
          "Estimasi harga untuk model ini belum tersedia — model tetap bisa dipakai, dan biaya sebenarnya tetap dicatat setelah tiap panggilan.",
        priceEstimateNote:
          "Estimasi per 1jt token pada kurs saat ini — bukan biaya per request.",
      },
      system: {
        eyebrow: "SISTEM",
        label: "System prompt",
        locked: "dikunci oleh workspace",
        empty: "Belum ada system prompt — klik untuk menambah.",
        skillPick: "Ambil dari skill",
        skillChange: "Ganti skill",
        skillNone: "Belum ada skill di workspace ini. Buat di halaman Skill.",
      },
      empty: {
        title: "Kirim pesan untuk memulai.",
        body: "Pre-flight governance berjalan setiap giliran — deteksi PII, kebijakan, routing, panggilan provider, dan audit.",
      },
      user: {
        label: "kamu",
        piiRedacted: "{n} PII diredaksi",
      },
      assistant: {
        brand: "Monago",
        thinking: "berpikir…",
        policyBlocked: "Permintaan diblokir oleh kebijakan",
        policyFlag: "policy flag",
        auditLink: "audit:{id}…",
        copy: "Salin",
        copied: "Tersalin",
      },
      noCredential: {
        title: "Hubungkan penyedia untuk menggunakan playground",
        body: "Playground hanya menggunakan kredensial penyedia Anda sendiri — tidak ada fallback ke kunci bersama. Tambahkan kunci OpenAI atau Anthropic di Penyedia, lalu kembali ke sini.",
        cta: "Buka Penyedia",
      },
    },
    governance: {
      panelTitle: "Pipeline governance",
      expandAriaLabel: "Buka panel governance",
      collapseAriaLabel: "Tutup panel governance",
      awaitingTrace: "menunggu trace…",
      totalMs: "{ms} ms total",
      flaggedCount: "{n} flagged",
      railLabel: "Governance",
      railLabelFlagged: "Governance · {n} flagged",
      stepLabels: {
        policy: "Policy Check",
        pii: "PII Detection",
        routing: "Routing",
        provider: "Provider Call",
        audit: "Audit Log",
      },
      statusPills: {
        ok: "ok",
        running: "running",
        flagged: "flagged",
        blocked: "blocked",
        pending: "pending",
      },
      summary: {
        latency: "LATENCY",
        tokens: "TOKENS",
        cost: "COST",
        audit: "AUDIT",
        viewAuditTrail: "Lihat audit trail lengkap ↗",
        awaitingAuditId: "Menunggu audit id…",
      },
      firstToken: "Token pertama",
      streaming: "Streaming",
      total: "Total",
    },
  },
  skills: {
    page: {
      title: "Skill",
      description: "Skill adalah konfigurasi spesialis (system prompt + parameter) yang versinya immutable, terhubung ke audit, dan dapat dievaluasi.",
      newButton: "Skill baru",
      fromTemplate: "Buat dari template",
      loading: "Memuat skill…",
      confirmDelete: "Hapus skill ini? Audit row lama tetap tersimpan.",
      expand: "Buka riwayat versi",
      collapse: "Tutup riwayat versi",
      activeTag: "aktif",
      activeVersionTitle: "Versi aktif v{v}",
      versionCount: "{n} versi total",
      lastUpdated: "diperbarui {when}",
      evalLink: "Status evaluasi",
      tryInPlayground: "Coba di Playground",
      editButton: "Edit",
      deleteButton: "Hapus",
      versionHistory: "Riwayat versi",
      versionsLoading: "Memuat riwayat versi…",
      versionsEmpty: "Belum ada versi yang tersimpan.",
      promoteButton: "Jadikan aktif",
      emptyTitle: "Belum ada skill di workspace ini",
      emptyBody: "Buat dari salah satu template tata kelola AI di bawah, atau mulai dari kosong. Skill bisa dipakai di Playground dan diuji di halaman Evaluasi.",
      emptyCreate: "Mulai dari kosong",
    },
    form: {
      name: "Nama",
      slug: "Slug",
      slugHint: "Opsional. Otomatis dibuat dari nama bila dikosongkan.",
      slugPlaceholder: "contoh: ai-governance-policy-advisor",
      description: "Deskripsi",
      systemPrompt: "System prompt",
      systemPromptTemplateHint: "Template terisi otomatis. Sesuaikan sebelum menyimpan sebagai v1.",
      model: "Model preferensi",
      modelNoPreference: "Tanpa preferensi (gunakan model sesi)",
      modelInactive: "{model} (tidak aktif)",
      modelInactiveHint: "Penyedia untuk model ini sedang tidak aktif. Pilih model lain atau pertahankan tanpa preferensi.",
      modelNoProviders: "Belum ada penyedia AI aktif.",
      modelNoProvidersCta: "Tambah penyedia →",
      temperature: "Temperature",
      notes: "Catatan versi",
      cancel: "Batal",
      close: "Tutup",
      saveNew: "Buat skill",
      saving: "Menyimpan…",
      blankDescription: "Bikin skill baru dari awal.",
      templateDescription: "Form ini sudah terisi dari template. Edit sesuai kebutuhan, lalu simpan sebagai v1.",
    },
    editDialog: {
      title: "Edit skill",
      description: "Ubah nama/deskripsi, atau buat versi baru. Versi lama tetap tersimpan; aktifkan dari riwayat versi.",
      metadataHeading: "Metadata skill",
      saveMetadata: "Simpan metadata",
      savingMetadata: "Menyimpan…",
      newVersionHeading: "Versi baru (akan jadi v{v})",
      createVersion: "Buat versi baru",
      creatingVersion: "Membuat…",
      notesPlaceholder: "Apa yang berubah di versi ini?",
      versionsNote: "Versi baru tidak otomatis aktif — promosikan dari kartu skill di halaman Skill.",
    },
  },
  evaluation: {
    page: {
      title: "Evaluasi",
      description: "Uji skill dengan skenario governance — input dijalankan melalui pipeline asli, lalu kita assert pada governance trace yang sebenarnya.",
      costNotice: "Setiap run menjalankan permintaan asli melalui gateway (BYOK). k attempts = k permintaan provider. pass^k bila semua harus lolos; pass@k bila cukup satu.",
      skillsLoading: "Memuat skill…",
      noSkills: "Belum ada skill di workspace ini. Buat skill di playground terlebih dahulu.",
    },
    picker: {
      skill: "Skill",
      version: "Versi yang diuji",
      versionHint: "Versi aktif: v{v}.",
      refreshSkills: "Refresh daftar skill",
    },
    scenarios: {
      heading: "{n} skenario",
      newButton: "Skenario baru",
      editButton: "Edit",
      deleteButton: "Hapus",
      confirmDelete: "Hapus skenario ini?",
      loading: "Memuat skenario…",
      emptyTitle: "Belum ada skenario untuk skill ini",
      emptyBody: "Buat skenario governance — input yang harus memicu redaksi PII, deny policy, atau prompt-injection.",
      noRunsYet: "Belum pernah dijalankan untuk versi ini.",
      expand: "Buka detail",
      collapse: "Tutup detail",
    },
    category: {
      pii: "PII",
      policy: "POLICY",
      security: "SECURITY",
    },
    run: {
      runButton: "Jalankan",
      running: "Menjalankan…",
      batchButton: "Jalankan semua",
      batchRunning: "Sedang menjalankan…",
      confirmSingle: "Jalankan skenario sebanyak {n} attempt (= {n} permintaan provider, BYOK)?",
      confirmBatch: "Jalankan {scenarios} skenario dengan total {n} attempt? Semua adalah permintaan provider asli.",
    },
    result: {
      never: "Belum dijalankan",
      title: "{result} · {passes}/{k} attempt lolos",
    },
    evidence: {
      heading: "Run terbaru · v{version} · {when}",
      attemptPass: "Attempt {n} — PASS",
      attemptFail: "Attempt {n} — FAIL",
    },
    history: {
      heading: "Riwayat run",
      loading: "Memuat riwayat…",
      empty: "Belum ada riwayat run untuk skill ini.",
    },
    summary: {
      piiFlagged: "harus terdeteksi PII",
      piiClean: "tidak boleh terdeteksi PII",
      piiTypes: "tipe PII mencakup {types}",
      policyBlocked: "harus diblok policy",
      securityFlagged: "harus dipicu security (mis. prompt injection)",
      noContent: "tidak ada konten yang boleh keluar",
      empty: "Belum ada assertion.",
    },
    form: {
      createTitle: "Skenario baru",
      editTitle: "Edit skenario",
      description: "Skenario menjalankan input ke pipeline asli skill, lalu assert pada governance trace yang dihasilkan.",
      name: "Nama",
      category: "Kategori",
      inputMessage: "Pesan user",
      expectHeading: "Yang harus terjadi (expect)",
      expectPiiFlagged: "PII terdeteksi",
      expectAny: "tidak ditentukan",
      expectYes: "ya",
      expectNo: "tidak",
      expectPiiTypes: "Tipe PII wajib (opsional)",
      expectPiiTypesHint: "Pisahkan dengan koma — case-insensitive. Contoh: NIK, EMAIL.",
      expectPolicyBlocked: "Policy harus memblok",
      expectSecurityFlagged: "Security flag (mis. prompt injection)",
      expectNoContent: "Tidak boleh ada konten yang keluar (hard block)",
      metric: "Metrik",
      metricCaret: "pass^k (semua lolos)",
      metricAt: "pass@k (cukup satu)",
      k: "k",
      kHint: "Min 1, maks 10. k = jumlah attempt = jumlah permintaan provider.",
      cancel: "Batal",
      save: "Simpan",
      saving: "Menyimpan…",
      errorEmptyExpect: "Minimal satu assertion harus dipilih.",
    },
  },
  errors: {
    notFound: {
      title: "Halaman tidak ditemukan",
      code: "404",
      description:
        "Halaman yang Anda cari tidak tersedia. Tautannya mungkin sudah berubah atau dihapus.",
      backHome: "Kembali ke dashboard",
    },
  },
  shared: {
    skipToContent: "Lompat ke konten utama",
    pagination: {
      rangeLabel: "Menampilkan baris {start}–{end}",
      previous: "Sebelumnya",
      next: "Selanjutnya",
    },
    errorPanel: {
      defaultTitle: "Gagal memuat data",
    },
    passwordInput: {
      show: "Tampilkan kata sandi",
      hide: "Sembunyikan kata sandi",
    },
    loading: {
      default: "Memuat",
    },
    breadcrumb: {
      ariaLabel: "Navigasi remah",
    },
    jsonEditor: {
      valid: "JSON valid",
      invalid: "JSON tidak valid",
      prompt: "Masukkan JSON",
      format: "Rapikan",
    },
    topbar: {
      openMenu: "Buka menu",
    },
  },

  platform: {
    tenants: {
      page: {
        title: "Tenant",
        description:
          "Provision tenant POC / sandbox, pantau masa trial, perpanjang atau konversi trial aktif. Khusus super-admin.",
        createButton: "Provision tenant",
      },
      list: {
        title: "Semua tenant",
        description:
          "Tampilan lintas-tenant. Hitung mundur trial diturunkan dari `trial_expires_at`; tenant tanpa deadline berarti unlimited (paid / internal).",
        colName: "Nama",
        colSlug: "Slug",
        colTrialStatus: "Trial",
        colCreated: "Dibuat",
        colActions: "Aksi",
        empty:
          "Belum ada tenant. Gunakan Provision tenant untuk membuat yang pertama.",
        statusUnlimited: "Unlimited",
        statusTrial: "Trial · {days} hari lagi",
        statusExpired: "Expired {days} hari lalu",
        extendButton: "Perpanjang",
        extendUnavailable:
          "Tenant unlimited — perpanjangan tidak berlaku.",
      },
      create: {
        title: "Provision tenant",
        description:
          "Membuat tenant + admin pertama + workspace default dalam satu panggilan. Password sementara di-generate dan ditampilkan sekali.",
        nameLabel: "Nama tenant",
        slugLabel: "Slug",
        slugHint:
          "Huruf kecil, angka, tanda hubung. Otomatis dari nama.",
        regionLabel: "Kode region",
        regionHint: "mis. id, sg, my, th, ph.",
        trialDaysLabel: "Hari trial",
        trialDaysHint: "Antara 1 dan 365. Default 45.",
        adminEmailLabel: "Email admin",
        adminNameLabel: "Nama lengkap admin",
        passwordHint:
          "Password sementara 20 karakter di-generate otomatis. Akan ditampilkan sekali di layar berikutnya — salin sebelum ditutup.",
        cancel: "Batal",
        submit: "Provision",
        submitLoading: "Provisioning...",
      },
      provisionResult: {
        title: "Tenant berhasil di-provision",
        description:
          "Serahkan kredensial ini ke admin {tenant}. Password sementara hanya ditampilkan sekali.",
        emailLabel: "Email admin",
        passwordLabel: "Password sementara",
        copy: "Salin",
        copied: "Tersalin",
        warningHeading: "Penting:",
        warningBody:
          "Password ini hanya ditampilkan sekali. Monago tidak menyimpan plaintext-nya dan tidak dapat menampilkannya kembali. Admin wajib menggantinya saat login pertama.",
        trialEndsLabel: "Trial berakhir",
        slugLabel: "Slug tenant",
        acknowledge:
          "Saya sudah menyalin kredensial dan menyimpannya di tempat aman.",
        closeButton: "Sudah disimpan, tutup",
      },
      extend: {
        title: "Perpanjang trial",
        description: "Tambah hari ke masa trial {tenant}.",
        daysLabel: "Tambahan hari",
        reasonLabel: "Alasan (opsional)",
        reasonHint:
          "Metadata audit — menjelaskan perpanjangan untuk review nanti.",
        cancel: "Batal",
        submit: "Perpanjang",
        submitLoading: "Memproses...",
      },
    },
  },
} as const;
